import React from "react";
import { getClassName } from "@utils";
import css from "./SignalItem.module.scss";
import {
  Skeleton,
  SkeletonSize,
  SkeletonVariant,
  Tooltip,
  TooltipPosition,
  TooltipSize,
} from "@taskpane/components";
import { FireIcon } from "./icons";

export interface SignalItemProps {
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  id?: string;
  isLoading?: boolean;
  opensCount?: number;
  subject?: string;
  time?: string;
  timeTooltip?: string;
  title?: string;
  onClick?: (id: string) => void;
}

const SignalItem: React.FC<SignalItemProps> = ({
  icon: IconComponent,
  id,
  isLoading = false,
  opensCount,
  subject,
  time,
  timeTooltip,
  title,
  onClick,
}) => {
  const isLabelActive = opensCount >= 7;
  const labelText =
    typeof opensCount === "number" ? `${opensCount} time${opensCount > 1 ? "s" : ""}` : "";

  const attrs = {
    container: {
      "aria-disabled": isLoading,
      className: css.container,
      onClick: () => {
        if (!isLoading && onClick && id) {
          onClick(id);
        }
      },
    },
    iconWrapper: {
      className: css.iconWrapper,
    },
    icon: {
      className: css.icon,
    },
    textWrapper: {
      className: css.textWrapper,
    },
    titleWrapper: {
      className: getClassName(css.titleWrapper, isLoading && css.titleWrapperLoading),
    },
    title: {
      className: css.title,
    },
    time: {
      className: css.time,
    },
    timeTooltip: {
      arrow: false,
      className: css.timeTooltip,
      position: TooltipPosition.TopEnd,
      positionOffset: 4,
      size: TooltipSize.XS,
      text: timeTooltip,
    },
    label: {
      className: getClassName(css.label, isLabelActive && css.labelActive),
    },
    subject: {
      className: css.subject,
    },
    iconSkeleton: {
      variant: SkeletonVariant.Circle,
      size: SkeletonSize.S,
    },
    timeSkeleton: {
      width: 48,
      className: css.timeSkeleton,
    },
    subjectSkeleton: {
      size: SkeletonSize.S,
    },
  };

  if (isLoading) {
    return (
      <div {...attrs.container}>
        <div {...attrs.iconWrapper}>
          <Skeleton {...attrs.iconSkeleton} />
        </div>
        <div {...attrs.textWrapper}>
          <div {...attrs.titleWrapper}>
            <Skeleton />
            <Skeleton {...attrs.timeSkeleton} />
          </div>
          <Skeleton {...attrs.subjectSkeleton} />
        </div>
      </div>
    );
  }

  return (
    <div {...attrs.container}>
      <div {...attrs.iconWrapper}>{IconComponent && <IconComponent {...attrs.icon} />}</div>
      <div {...attrs.textWrapper}>
        <div {...attrs.titleWrapper}>
          <span {...attrs.title}>{title}</span>
          {labelText ? (
            <span {...attrs.label}>
              {isLabelActive ? <FireIcon /> : null}
              <span>{labelText}</span>
            </span>
          ) : null}
          <Tooltip {...attrs.timeTooltip}>
            <span {...attrs.time}>{time}</span>
          </Tooltip>
        </div>
        {subject ? <span {...attrs.subject}>{subject}</span> : null}
      </div>
    </div>
  );
};

export default SignalItem;
